import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { loadDb } from './firebase';
import type { AnalysisKind } from './types';

/**
 * The signed-in user's map history, read straight from Firestore.
 *
 * The API writes one document per finished job into `maps/`; the security
 * rules let a user read only documents whose `owner_uid` is their own. Reading
 * here rather than through the API means the list updates by itself when a
 * share toggle or a new job lands.
 *
 * Firestore is imported dynamically for the same reason Auth is: a visitor who
 * only opens a shared map should not download either.
 */

export interface SavedMap {
  id: string;
  name: string;
  analysis: AnalysisKind | string;
  sourceKind: string;
  nItems: number;
  nLinks: number;
  nClusters: number;
  nDocuments: number;
  createdAt: Date | null;
  shared: boolean;
  shareId: string | null;
}

/** Firestore documents are `DocumentData`, so every field is checked. */
function num(data: DocumentData, key: string): number {
  const value: unknown = data[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function text(data: DocumentData, key: string, fallback = ''): string {
  const value: unknown = data[key];
  return typeof value === 'string' ? value : fallback;
}

function toDate(value: unknown): Date | null {
  if (value && typeof value === 'object' && 'toDate' in value) {
    const asTimestamp = value as { toDate: () => Date };
    try {
      return asTimestamp.toDate();
    } catch {
      return null;
    }
  }
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function toSavedMap(snapshot: QueryDocumentSnapshot<DocumentData>): SavedMap {
  const data = snapshot.data();
  const shareId = data['share_id'];
  return {
    id: snapshot.id,
    name: text(data, 'name', snapshot.id),
    analysis: text(data, 'analysis', 'co_authorship'),
    sourceKind: text(data, 'source_kind', 'openalex'),
    nItems: num(data, 'n_items'),
    nLinks: num(data, 'n_links'),
    nClusters: num(data, 'n_clusters'),
    nDocuments: num(data, 'n_documents'),
    createdAt: toDate(data['created_at']),
    shared: data['shared'] === true,
    shareId: typeof shareId === 'string' && shareId.length > 0 ? shareId : null,
  };
}

/** How many rows the history page shows. Beyond this the user is better served
 *  by a search endpoint, which this deployment does not have. */
export const SAVED_MAPS_LIMIT = 200;

/**
 * Subscribe to the user's maps, newest first. Returns the unsubscribe function
 * straight away; the subscription itself is set up once the SDK has loaded.
 * Needs the composite index on (`owner_uid` asc, `created_at` desc), which
 * lives in firestore.indexes.json.
 */
export function watchSavedMaps(
  uid: string,
  onData: (maps: SavedMap[]) => void,
  onError: (error: Error) => void,
): () => void {
  let unsubscribe: (() => void) | null = null;
  let cancelled = false;

  void (async () => {
    try {
      const [firestore, db] = await Promise.all([import('firebase/firestore'), loadDb()]);
      if (cancelled) return;
      const { collection, limit, onSnapshot, orderBy, query, where } = firestore;
      const q = query(
        collection(db, 'maps'),
        where('owner_uid', '==', uid),
        orderBy('created_at', 'desc'),
        limit(SAVED_MAPS_LIMIT),
      );
      unsubscribe = onSnapshot(
        q,
        (snapshot) => onData(snapshot.docs.map(toSavedMap)),
        (error) => onError(error),
      );
    } catch (cause) {
      if (cancelled) return;
      onError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  })();

  return () => {
    cancelled = true;
    unsubscribe?.();
  };
}
